/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import { Chart } from '@scuf/charts';
import { useTranslation } from '@scuf/localization';
import './UtilityTrendChart.scss';
import '../../../scss/TooltipCommonTheme.scss';
import { ITrendChartProps } from './ITrendChartProps';
import {
  formatCurrency,
  getSymbolFromCurrency,
  actualValueCheck,
  baselineValueCheck,
  actualCostCheck,
  baselineCostCheck,
  baselineConsDataDisplay,
  actualConsDataDisplay
} from '../Utils/Utils';
import { baselineCostDisplay } from './TrendChartUtils';
import { MAGIC_VALUE } from '../Utils/Constants';
import {
  actualConsumptionDataValue,
  actualCostDataValue,
  baselineConsumptionDataValue,
  baselineCostDataValue,
  chartactualCostData
} from './UtilityTrendChartUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';

const UtilityTrendChart = (props: ITrendChartProps) => {
  const [t] = useTranslation();

  const {
    costCurrency,
    measurementUnit,
    chartData,
    legendItems,
    category
  } = props;
  const {
    baselineConsumptionData,
    baselineCostData,
    actualConsumptionData,
    actualCostData,
    IsCompleted,
    YearIndex,
    MonthIndex,
    PeriodUpto
  } = chartData;
  const [baselineL, actualL, baselineCostL, actualCostL] = legendItems;

  const renderTooltip = (details: IAny) => {
    {
      const { x }: { x: number } = details;
      const period = PeriodUpto[x];
      const tooltipMonth = MonthIndex[x];
      const tooltipYear = YearIndex[x];
      const actualValue = actualValueCheck(actualConsumptionData, x);
      const baselineValue = baselineValueCheck(baselineConsumptionData, x);
      const actualCost = actualCostCheck(actualCostData, x);
      const baselineCost = baselineCostCheck(baselineCostData, x);
      const partialDate = period;

      const isCompletedData = IsCompleted[x];
      if (details.label.includes('Actual') && !details.label.includes('Cost'))
        return (
          <div className="emaas-chart-widget-container">
            <div className="tooltip-top">
              <div>
                <span className="tooltip-date emaasThemeColor">
                  {tooltipMonth} {tooltipYear}
                </span>
              </div>
              <div className="tooltip-pane">
                {actualConsumptionDataValue({
                  actualConsumptionData,
                  category,
                  actualL,
                  isCompletedData,
                  partialDate,
                  t,
                  measurementUnit,
                  actualValue
                })}
                {baselineConsumptionDataValue(
                  baselineConsumptionData,
                  baselineL,
                  measurementUnit,
                  baselineValue,
                  t
                )}
                {actualCostDataValue(actualCostL, costCurrency, actualCost, t)}
                {baselineCostDataValue(
                  baselineCostData,
                  baselineCostL,
                  costCurrency,
                  baselineCost,
                  t
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  const currencySymbol = getSymbolFromCurrency(costCurrency ?? '');
  return (
    <div className="trend-chart-widget">
      <Chart
        className="trend-chart"
        colors={legendItems.map((item) => item.color)}
        spacing={[
          MAGIC_VALUE.THIRTY,
          MAGIC_VALUE.ZERO,
          MAGIC_VALUE.ZERO,
          MAGIC_VALUE.ZERO
        ]}
        showLegend={false}
      >
        {baselineConsDataDisplay(measurementUnit, baselineConsumptionData, t)}
        {actualConsDataDisplay(measurementUnit, actualConsumptionData, t)}
        {baselineCostDisplay(
          currencySymbol,
          baselineCostData,
          baselineConsumptionData,
          actualConsumptionData,
          t
        )}
        {chartactualCostData(
          actualCostData,
          currencySymbol,
          baselineConsumptionData,
          actualConsumptionData,
          t
        )}
        <Chart.XAxis
          lineColor="#d0d0d0"
          className="month-labels"
          allowDecimals={false}
          showGridLines={false}
          labels={{ autoRotation: false }}
          categories={MonthIndex}
        />
        {baselineConsumptionData || actualConsumptionData ? (
          <Chart.YAxis
            showGridLines={true}
            opposite={false}
            labelSuffix={`${' '}${measurementUnit}`}
            gridLineColor="#d0d0d0"
            gridLineWidth={0.5}
            className="trend-chart-yaxis"
            tickAmount={4}
          />
        ) : (
          undefined
        )}
        {baselineCostData || actualCostData ? (
          <Chart.YAxis
            gridLineColor="#d0d0d0"
            gridLineWidth={0}
            showGridLines={true}
            visible={true}
            opposite={true}
            labels={{
              formatter: function() {
                return formatCurrency(this.value, costCurrency, false);
              }
            }}
            className="trend-chart-yaxis"
            tickAmount={4}
          />
        ) : (
          undefined
        )}
        <Chart.Tooltip
          borderRadius={10}
          valueSuffix={measurementUnit}
          renderContent={renderTooltip}
          backgroundColor="white"
          shared={true}
        />
      </Chart>
      <div className="trend-chart-legends-container">
        <div className="legends-flex-container">
          {legendItems.map((legend) => {
            if (legend.type == 'Bar') {
              return (
                <div
                  className={`bar-chart-legend-container ${legend.className}`}
                  style={{
                    borderLeft: `0.15rem solid ${legend.color}`,
                    height: '1.25rem',
                    width: 'auto',
                    alignItems: 'center',
                    lineHeight: '1.25rem'
                  }}
                  key={legend.text}
                >
                  <span className="Bar-chart-legend-text">{legend.text}</span>
                </div>
              );
            }
            if (legend.type == 'Line') {
              return (
                <div
                  className={`line-chart-legend-container ${legend.className}`}
                  key={legend.text}
                >
                  <div
                    className="line-chart-legend-icon"
                    style={{ backgroundColor: legend.color }}
                  >
                    <span
                      className="dot"
                      style={{ backgroundColor: legend.color }}
                    ></span>
                  </div>
                  <span className="line-chart-legend-text">{legend.text}</span>
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default UtilityTrendChart;
